import { A11y } from 'swiper/modules';
import { Swiper } from 'swiper/react';

import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';

import { Button } from '$partials/ui/index';


/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

const CarouselContext = createContext();

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/base.d.ts').Carousel}
 */

const Carousel = ({
    children,
    swiperConfig = {}
}) => {
    // State
    const [config, setConfig] = useState(null);
    const [swiper, setSwiper] = useState(null);
    const [swiperIsEnd, setSwiperIsEnd] = useState(null);
    const [swiperIsStart, setSwiperIsStart] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);

    // Methods
    const updateSwiperState = (swpr) => {
        setSwiper(swpr);
        setSwiperIsEnd(swpr.isEnd);
        setSwiperIsStart(swpr.isBeginning);
        setActiveIndex(swpr.activeIndex);
    };

    // Hooks
    useEffect(() => {
        setConfig({
            modules: [A11y],
            onResize: updateSwiperState,
            onSwiper: updateSwiperState,
            onSlideChange: updateSwiperState,
            onTransitionEnd: updateSwiperState,
            wrapperTag: 'div',
            ...swiperConfig
        });
    }, [swiperConfig]);

    // Render
    return (
        <CarouselContext.Provider value={useMemo(() => {
            return {
                config,
                swiper,
                swiperIsEnd,
                swiperIsStart,
                activeIndex
            };
        }, [
            config,
            swiper,
            swiperIsEnd,
            swiperIsStart,
            activeIndex
        ])}>
            {children}
        </CarouselContext.Provider>
    );
};


/**
 * @type {import('$types/components/partials/base.d.ts').CarouselWrapper}
 */
const CarouselWrapper = ({ children, ...props }) => {
    // Contexts
    const cc = useContext(CarouselContext);

    // Context Check
    if (cc === undefined) {
        throw new Error('CarouselWrapper must be used within a CarouselContext.Provider');
    }
    // Render
    return (
        <Swiper
            {...cc.config}
            {...props}
            key={cc.config}>
            {children}
        </Swiper>
    );
};

const CarouselNavigation = () => {
    // Contexts
    const cc = useContext(CarouselContext);

    // Context Check
    if (cc === undefined) {
        throw new Error('CarouselNavigation must be used within a CarouselContext.Provider');
    }

    // Render
    return (
        <div
            className='absolute top-0 right-0 z-50 gap-x-5 flex flex-row py-10 px-15'
            role='group'>
            <Button
                aria-label='carousel navigation left'
                className={`rounded-full bg-white p-20 flex shadow-button ${cc.swiperIsStart ? '' : 'hover:bg-light-yellow'}`}
                disabled={cc.swiperIsStart}
                onClick={() => cc.swiper?.slidePrev()}
                type='button' />
            <Button
                aria-label='carousel navigation right'
                className={`rounded-full bg-white p-20 flex shadow-button ${cc.swiperIsEnd ? '' : 'hover:bg-light-yellow'}`}
                disabled={cc.swiperIsEnd}
                onClick={() => cc.swiper?.slideNext()}
                type='button' />
        </div>
    );
};

const CarouselArrowNavigation = () => {
    // Contexts
    const cc = useContext(CarouselContext);

    // Context Check
    if (cc === undefined) {
        throw new Error('CarouselNavigation must be used within a CarouselContext.Provider');
    }

    // Render
    return (
        <div
            className='flex flex-row'
            role='group'>
            <Button
                aria-label='carousel navigation left'
                className='button-icon-transparent'
                disabled={cc.swiperIsStart}
                onClick={() => cc.swiper?.slidePrev()}
                type='button' />
            <Button
                aria-label='carousel navigation right'
                className='button-icon-transparent'
                disabled={cc.swiperIsEnd}
                onClick={() => cc.swiper?.slideNext()}
                type='button' />
        </div>
    );
};

/**
 * @type {import('$types/components/partials/base.d.ts').CarouselPagination}
 */

const CarouselPagination = ({ totalSlides }) => {
    // Contexts
    const cc = useContext(CarouselContext);

    // Context Check
    if (cc === undefined) {
        throw new Error('CarouselPagination must be used within a CarouselContext.Provider');
    }

    if (!totalSlides || totalSlides <= 1) {
        return null;
    }

    // Render
    return (
        <div
            aria-label='carousel pagination'
            className='absolute bottom-24 left-1/2 transform -translate-x-1/2 z-50 flex gap-x-8 pb-20'
            role='group'>
            {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                    aria-label={`Go to slide ${index + 1}`}
                    className={`w-8 h-8 rounded-full transition-colors ${
                        index === cc.activeIndex ? 'bg-grey-grey-100' : 'bg-grey-grey-100/30'
                    }`}
                    key={index}
                    onClick={() => cc.swiper?.slideTo(index)}
                    type='button' />
            ))}
        </div>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

CarouselContext.displayName = 'CarouselContext';

export {
    Carousel,
    CarouselWrapper,
    CarouselArrowNavigation,
    CarouselNavigation,
    CarouselPagination,
    CarouselContext
};
